import React, { useEffect, useState } from 'react'
import { Box, InputAdornment } from '@mui/material'
import { AsYouType, type CountryCode } from 'libphonenumber-js'

import { FormHelperText, TextField } from '~components/common'
import { phoneValid } from '~types'

type CountryOptions = {
  code: CountryCode
  name: string
  emoji: string
  dialCode: string
  maxDigits: number
}

const countryOptions: [CountryOptions] = [
  { code: 'US', name: 'United States', emoji: '🇺🇸', dialCode: '+1', maxDigits: 10 },
  // NOTE: planning to modify PhoneNumber table before supporting international numbers
  // { code: 'GB', name: 'United Kingdom', emoji: '🇬🇧', dialCode: '+44', maxDigits: 10 },
  // { code: 'DE', name: 'Germany', emoji: '🇩🇪', dialCode: '+49', maxDigits: 11 },
]

const formatPhoneInput = (input: string | undefined, countryCode: CountryCode = 'US'): string => {
  if (!input) return ''
  const [number, ext = ''] = input.split('x')
  let numberStr = new AsYouType(countryCode).input(
    number.startsWith('+1') ? number.slice(2) : number,
  )
  if (ext) numberStr += ` ext. ${ext}`
  return numberStr
}

interface PhoneEntryBoxProps {
  inputPhoneState: string
  includeExt?: boolean
  onChange: (newPhoneState: string, newIsValid: boolean) => void
}

const PhoneEntryBox: React.FC<PhoneEntryBoxProps> = ({
  inputPhoneState,
  includeExt = true,
  onChange,
}) => {
  const country = countryOptions[0]
  const [phoneNumber, setPhoneNumber] = useState(formatPhoneInput(inputPhoneState, country.code))
  const [rawPhoneNumber, setRawPhoneNumber] = useState('')
  const [phoneExtension, setPhoneExtension] = useState('')
  const [blurred, setBlurred] = useState(false)

  const isValid = phoneValid(rawPhoneNumber)
  const showError = blurred && !isValid && rawPhoneNumber.length > 0

  const extractDigits = (input: string): string => {
    return input.replace(/\D/g, '')
  }

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>): void => {
    event.preventDefault()
    const pastedText = event.clipboardData.getData('text')
    let digits = extractDigits(pastedText)
    // For US numbers, handle case where first digit might be 0 or 1
    if (country.code === 'US' && digits.length > 0 && ['0', '1'].includes(digits[0])) {
      digits = digits.substring(1)
    }
    // Limit to max digits
    if (digits.length > country.maxDigits) {
      digits = digits.substring(0, country.maxDigits)
    }
    updatePhoneState(digits, phoneExtension)
  }

  const handlePhoneNumberChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const input = event.target.value
    let newRawPhoneNumber = rawPhoneNumber

    if (input.length < phoneNumber.length) {
      // Backspace was pressed
      newRawPhoneNumber = rawPhoneNumber.slice(0, -1)
    } else {
      // New digit was added
      const lastChar = input[input.length - 1]
      if (/\d/.test(lastChar) && newRawPhoneNumber.length < country.maxDigits) {
        // For US numbers, prevent 0 or 1 as the first digit
        if (
          country.code === 'US' &&
          newRawPhoneNumber.length === 0 &&
          ['0', '1'].includes(lastChar)
        ) {
          return
        }
        newRawPhoneNumber += lastChar
      }
    }
    updatePhoneState(newRawPhoneNumber, phoneExtension)
  }

  const handleExtensionChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const newExtension = event.target.value.replace(/\D/g, '')
    updatePhoneState(rawPhoneNumber, newExtension)
  }

  const handleBlur = (): void => {
    setBlurred(true)
  }

  const handleFocus = (): void => {
    setBlurred(false)
  }

  const updatePhoneState = (rawNumber: string, ext: string): void => {
    const newPhoneState = ext ? `${rawNumber}x${ext}` : rawNumber
    const newPhoneNumberValid = phoneValid(rawNumber)

    setRawPhoneNumber(rawNumber)
    setPhoneExtension(ext)
    setPhoneNumber(formatPhoneInput(rawNumber, country.code))
    onChange(newPhoneState, newPhoneNumberValid)
  }

  useEffect(() => {
    if (inputPhoneState) {
      const [number, ext = ''] = inputPhoneState.split('x')
      setRawPhoneNumber(number)
      setPhoneExtension(includeExt ? ext : '')
      setPhoneNumber(formatPhoneInput(number, country.code))
    } else {
      setRawPhoneNumber('')
      setPhoneExtension('')
      setPhoneNumber('')
    }
  }, [inputPhoneState, country.code])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <TextField
          value={phoneNumber}
          onChange={handlePhoneNumberChange}
          onPaste={handlePaste}
          onBlur={handleBlur}
          onFocus={handleFocus}
          placeholder={`(XXX) XXX-XXXX`}
          error={showError}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <span style={{ marginRight: 8 }}>{country.emoji}</span>
                {country.dialCode}
              </InputAdornment>
            ),
          }}
        />
        {includeExt && (
          <TextField
            value={phoneExtension}
            onChange={handleExtensionChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            placeholder='Ext.'
            style={{ width: '80px' }}
          />
        )}
      </Box>
      {showError && <FormHelperText error>Please enter a valid phone number</FormHelperText>}
    </Box>
  )
}

export default PhoneEntryBox
